import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaHome, FaUsers, FaLink, FaGavel, FaCheckCircle,
  FaDollarSign, FaSignOutAlt, FaBars, FaTimes, FaUser,
  FaCog, FaBolt, FaChevronDown, FaChevronRight, FaUserTie, FaFileAlt, FaPlus, FaAddressBook
} from 'react-icons/fa';
import SessionTimeout from './SessionTimeout';
import './Layout.css';

const Layout = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState(['administration']);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (onLogout) onLogout();
      navigate('/login');
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev =>
      prev.includes(categoryKey)
        ? prev.filter(key => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const navStructure = [
    {
      type: 'item',
      path: '/dashboard',
      icon: FaHome,
      label: 'Dashboard'
    },
    {
      type: 'item',
      path: '/defendants',
      icon: FaUsers,
      label: 'Defendants'
    },
    {
      type: 'item',
      path: '/bonds',
      icon: FaLink,
      label: 'Bonds'
    },
    {
      type: 'item',
      path: '/court-dates',
      icon: FaGavel,
      label: 'Court Dates'
    },
    {
      type: 'item',
      path: '/checkins',
      icon: FaCheckCircle,
      label: 'Check-ins'
    },
    {
      type: 'item',
      path: '/payments',
      icon: FaDollarSign,
      label: 'Payments'
    },
    {
      type: 'item',
      path: '/address-book',
      icon: FaAddressBook,
      label: 'Address Book'
    },
    {
      type: 'divider'
    },
    {
      type: 'item',
      path: '/profile',
      icon: FaUser,
      label: 'My Profile'
    },
    {
      type: 'divider'
    },
    ...(user?.role === 'admin' ? [{
      type: 'category',
      key: 'administration',
      icon: FaCog,
      label: 'Administration',
      items: [
        {
          path: '/admin/powers',
          icon: FaBolt,
          label: 'Powers'
        },
        {
          path: '/admin/staff',
          icon: FaUserTie,
          label: 'Staff'
        },
        {
          path: '/admin/charges',
          icon: FaFileAlt,
          label: 'Charges'
        },
        {
          path: '/admin/users',
          icon: FaUser,
          label: 'Users'
        }
      ]
    }] : [])
  ];

  const renderNavItem = (item, index) => {
    if (item.type === 'divider') {
      return <div key={`divider-${index}`} className="nav-divider" />;
    }

    if (item.type === 'category') {
      const isExpanded = expandedCategories.includes(item.key);
      const hasActiveChild = item.items.some(child =>
        location.pathname.startsWith(child.path)
      );
      const CategoryIcon = item.icon;

      return (
        <div key={item.key} className="nav-category">
          <div
            className={`nav-category-header ${hasActiveChild ? 'active' : ''}`}
            onClick={() => toggleCategory(item.key)}
          >
            <div className="nav-category-label">
              <CategoryIcon className="nav-icon" />
              {sidebarOpen && <span>{item.label}</span>}
            </div>
            {sidebarOpen && (
              isExpanded ? <FaChevronDown className="chevron" /> : <FaChevronRight className="chevron" />
            )}
          </div>
          {isExpanded && sidebarOpen && (
            <div className="nav-category-items">
              {item.items.map((subItem) => {
                const SubIcon = subItem.icon;
                const isActive = location.pathname.startsWith(subItem.path);
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`nav-item nav-subitem ${isActive ? 'active' : ''}`}
                  >
                    <SubIcon className="nav-icon" />
                    <span className="nav-label">{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Regular nav item
    const Icon = item.icon;
    const isActive = location.pathname.startsWith(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        className={`nav-item ${isActive ? 'active' : ''}`}
      >
        <Icon className="nav-icon" />
        {sidebarOpen && <span className="nav-label">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="layout">
      {/* Session Timeout Monitor */}
      <SessionTimeout onLogout={onLogout} timeoutMinutes={30} />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            <img
              src="/logo-text.png"
              alt="Bad Boy Bail Bonds"
              className="sidebar-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="sidebar-logo-text" style={{ display: 'none' }}>
              <h2>BAD BOY</h2>
              <p>BAIL BONDS</p>
            </div>
          </div>

          {/* Start New Bond Button */}
          <Link to="/bonds/new" className="start-bond-btn">
            <FaPlus className="nav-icon" />
            {sidebarOpen && <span>Start New Bond</span>}
          </Link>
        </div>

        <nav className="sidebar-nav">
          {navStructure.map((item, index) => renderNavItem(item, index))}
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-btn" onClick={handleLogout}>
            <FaSignOutAlt className="nav-icon" />
            {sidebarOpen && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <header className="topbar">
          <button className="menu-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className="topbar-right">
            <div className="user-info">
              <div className="user-avatar">
                <FaUser />
              </div>
              <div className="user-details">
                <span className="user-name">
                  {user?.first_name} {user?.last_name}
                </span>
                <span className="user-role">{user?.role}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="footer">
          <p>© 2025 Bad Boy Bail Bonds - Montana Pretrial Services LLC. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
